/**
 * Logging a user out from outside React (the axios interceptor), used when Maestro reports that the
 * Kinde account is suspended.
 *
 * Redirecting a suspended user to /login does not help: the Kinde SDK silently restores the session
 * (refresh tokens ignore suspension), Login sends the user back to the app, the app's first requests
 * get 401 again — an endless reload loop. Logging out ends the Kinde session, so the user stays out
 * and Kinde itself refuses the next sign-in while the account is suspended.
 */

export const ACCOUNT_SUSPENDED_ERROR_CODE = 'ACCOUNT_SUSPENDED';

let logoutHandler: (() => Promise<void>) | null = null;
let isForcingLogout = false;

/** Registered by KindeWrapper, which owns the Kinde logout. */
export const setLogoutHandler = (handler: (() => Promise<void>) | null) => {
  logoutHandler = handler;
};

/** Logs out once, however many requests fail at the same time. */
export const forceLogout = async () => {
  if (isForcingLogout || !logoutHandler) {
    return;
  }
  isForcingLogout = true;
  await logoutHandler();
};

/** The API marks suspension with errors[0].errorCode; older backends only send the message. */
export const isAccountSuspended = (data: any): boolean =>
  (Array.isArray(data?.error?.errors) &&
    data.error.errors.some((e: any) => e?.errorCode === ACCOUNT_SUSPENDED_ERROR_CODE)) ||
  data?.error?.message === 'Account suspended';

/** Test-only: reset module state between tests. */
export const resetForceLogoutForTests = () => {
  logoutHandler = null;
  isForcingLogout = false;
};
